<template>
  <v-form ref="addCodeForm"
          :disabled="isFormDisabled()"
  >
    <v-row>
      <v-col cols="12" sm="3" lg="2">
        <v-text-field
            v-model="details.code"
            label="Kod"
            :aria-required="true"
            :loading="generating"
            :disabled="generating"
        >
        </v-text-field>
      </v-col>
      <v-col cols="12" sm="3" lg="1">
        <v-text-field
            v-model="codeLength"
            type="number"
            min="4"
            max="20"
            label="Długość"
        >
          <template
              v-slot:append-outer
              v-if="!isEditForm()"
          >
            <v-icon dark color="primary"
                    @click="generate"
                    attach=""
                    title="Wyślij do serwera żądanie wygenerowania i zapisania nowego kodu."
            >
              mdi-refresh
            </v-icon>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="12" sm="9" lg="3">
        <v-text-field
            v-model="details.nip"
            label="Numer NIP"
            hint="Pozostaw puste, jeśli kod ma być dostępny dla wszystkich firm."
            :persistent-hint="true"
            :rules="rules.nip"
            counter="10"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-text-field
            type="number"
            v-model="details.maxCompanies"
            placeholder="Liczba całkowita większa od zera"
            hint="Określa ile firm może skorzystać z tego kodu."
            :label="label.maxCompanies"
            :persistent-hint="true"
            :rules="rules.maxCompanies"
            :aria-required="true"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" lg="3">
        <v-select
            v-model="details.type"
            label="Typ kodu"
            :items="types"
            item-value="value"
            item-text="text"
            @change="onTypeChanged"
        ></v-select>
      </v-col>
      <v-col cols="12" lg="6">
        <v-textarea
            v-model="details.message"
            label="Komunikat dla użytkownika"
            rows="2"
            placeholder=" "
            hint="Wyświetlany, gdy kod jest prawidłowy i można go użyć."
            :persistent-hint="true"
            counter="65535"
        ></v-textarea>
      </v-col>
      <v-col cols="12" lg="6">
        <v-textarea
            v-model="details.notes"
            label="Notatki dla administratorów"
            rows="2"
            placeholder=" "
            counter="65535"
        ></v-textarea>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="text-center">
        <v-btn
            :disabled="generating"
            @click="save"
            v-if="!isEditForm()"
        >
          Dodaj
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import api from "@/services/api";
import {CodeType, getItems as getCodeTypeItems} from '@/enums/CodeType';

export default {
  props: {
    code: {
      type: [String],
      required: false,
      default: null,
    }
  },
  data() {
    return {
      default: {
        maxCompanies: {
          [CodeType.confirming]: 10,
          [CodeType.customer_acquisition]: 100,
          [CodeType.promotional]: 10,
          other: 10,
        },
        type: 'confirming',
      },
      details: {
        code: '',
        nip: '',
        maxCompanies: 10,
        type: 'confirming',
        status: null, // Reserved for future use.
        message: '',
        notes: '',
      },
      types: getCodeTypeItems(),
      label: {
        maxCompanies: 'Maks. ilość firm',
      },
      rules: {
        nip: [
          (value) => {
            if (undefined === value || value.trim() === '') {
              return true; // Allow this field to be empty.
            }

            if (!/^\d+$/.test(value)) {
              return 'Numer NIP musi składać się wyłącznie z cyfr.';
            }

            return 10 === value.length || 'Numer NIP musi mieć 10 cyfr.';
          },
        ],
        maxCompanies: [
          (value) => {
            const max = 32767;
            value = parseInt(value, 10);

            if (value > 0 && value <= max) {
              return true;
            }

            return `Wprowadź wartość z zakresu 1 - ${max}.`;
          }
        ]
      },
      generating: false,
      loading: false,
      saving: false,
      codeLength: 8,
    };
  },
  computed: {
    defaultMaxCompanies() {
      const number = this.default.maxCompanies[CodeType[this.details.type]];

      if (number) {
        return number;
      }

      return this.default.maxCompanies.other;
    }
  },
  created() {
    if (this.code) {
      this.get();
    } else {
      this.generate();
    }
  },
  methods: {
    isFormDisabled() {
      return this.saving || !!this.code;
    },
    isEditForm() {
      return !!this.code;
    },
    generate() {
      if (this.generating) {
        return;
      }

      this.generating = true;

      api.post('/api/codes', {length: this.codeLength})
          .then((response) => {
            this.details.code = response.data.code;
          })
          .catch((error) => {
            this.$root.$emit('notifyError', {
              html: 'Nie udało się wygenerować kodu.',
              error,
            });
          })
          .finally(() => {
            this.generating = false;
          });
    },
    get() {
      if (this.loading) {
        return;
      }

      this.loading = true;

      api.get(`/api/codes/${this.code}`)
          .then((response) => {
            this.details = response.data;
          })
          .catch((error) => {
            this.$root.$emit('notifyError', {
              html: 'Nie udało się pobrać szczegółów kodu.',
              error,
            });
          })
          .finally(() => {
            this.loading = false;
          });
    },
    save() {
      if (this.saving) {
        return;
      }

      this.saving = true;
      const self = this;

      api.put(`/api/codes/${this.details.code}`, this.details)
          .then((response) => {
            this.details = response.data;
            this.$root.$emit('notifySuccess', {
              html: 'Kod został zapisany.',
              callback: () => {
                // The following setTimeout() call is required to render the success message for the user before
                // displaying the built-in prompt window.
                setTimeout(function () {
                  self.saving = false;
                  self.askForNextCode();
                }, 500);
              }
            });
          })
          .catch((error) => {
            this.saving = false;
            this.$root.$emit('notifyError', {
              html: 'Nie udało się dodać kodu.',
              error,
            });
          });
    },
    askForNextCode() {
      if (confirm('Chcesz dodać kolejny kod?')) {
        this.clear();
      } else {
        this.$router
            .push({name: 'codes'})
            .catch(() => {
            });
      }
    },
    onTypeChanged() {
      const oldMax = this.details.maxCompanies;
      const newMax = this.defaultMaxCompanies;

      if (oldMax === newMax) {
        return;
      }

      this.details.maxCompanies = newMax;
      this.$root.$emit('notify', {
        html: `Zmieniono domyślną wartość w polu "${this.label.maxCompanies}" z ${oldMax} na ${newMax}.`,
      });
    },
    clear() {
      this.details.nip = '';
      this.type = this.default.type;
      this.details.maxCompanies = this.defaultMaxCompanies;
      // Do not clear "message" and "notes" - this may be useful for Admins.
      this.generate();
    }
  },
}
</script>